import { MenuOutlined } from "@ant-design/icons";
import { Button, Drawer, Grid, Layout, Menu } from "antd";
import { useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { ThemeToggle } from "@/shared/components/ui";
import { Brand } from "./Brand";
import { findNavItem, navSections } from "./navigation";
import { UserMenu } from "./UserMenu";

const { Sider, Header, Content } = Layout;

const SIDER_WIDTH = 240;

export function MainLayout() {
	const navigate = useNavigate();
	const location = useLocation();
	const screens = Grid.useBreakpoint();
	const isMobile = !screens.lg;

	const [drawerOpen, setDrawerOpen] = useState(false);
	const activeItem = findNavItem(location.pathname);

	const handleNavigate = (path: string) => {
		navigate(path);
		// On mobile the nav lives in a drawer, which should close after a jump.
		setDrawerOpen(false);
	};

	const menu = (
		<Menu
			mode="inline"
			selectedKeys={activeItem ? [activeItem.path] : []}
			onClick={({ key }) => handleNavigate(key)}
			items={navSections.map((section) => ({
				key: section.title,
				type: "group" as const,
				label: (
					<span className="text-[11px] font-medium uppercase tracking-wider text-[var(--c-text-muted)]">
						{section.title}
					</span>
				),
				children: section.items.map((item) => ({
					key: item.path,
					icon: item.icon,
					label: item.label,
				})),
			}))}
		/>
	);

	const sidebar = (
		<div className="flex h-full flex-col bg-[var(--c-chrome)]">
			<Brand />
			<div className="min-h-0 flex-1 overflow-y-auto py-2">{menu}</div>
			<div className="border-t border-[var(--c-border-subtle)] p-2">
				<UserMenu />
			</div>
		</div>
	);

	return (
		<Layout className="min-h-screen">
			{isMobile ? (
				<Drawer
					open={drawerOpen}
					onClose={() => setDrawerOpen(false)}
					placement="left"
					width={SIDER_WIDTH}
					closable={false}
					styles={{ body: { padding: 0 } }}
				>
					{sidebar}
				</Drawer>
			) : (
				<Sider width={SIDER_WIDTH} theme="light" className="!fixed inset-y-0 left-0 z-20">
					{sidebar}
				</Sider>
			)}

			<Layout style={{ marginInlineStart: isMobile ? 0 : SIDER_WIDTH }}>
				{/* Ant sets `line-height: <headerHeight>` on Header, which would stretch the
				    two-line title stack — reset it here. */}
				<Header
					className="sticky top-0 z-10 flex items-center gap-2 border-b border-[var(--c-border)] !px-3 sm:gap-3 sm:!px-5"
					style={{ lineHeight: 1.4 }}
				>
					{isMobile ? (
						<Button
							type="text"
							aria-label="Menyu"
							icon={<MenuOutlined />}
							onClick={() => setDrawerOpen(true)}
						/>
					) : null}

					<div className="min-w-0 flex-1">
						<div className="truncate text-[13px] font-medium leading-tight text-[var(--c-text)]">
							{activeItem?.label ?? "Murojaat"}
						</div>
						{activeItem?.description ? (
							<div className="hidden truncate text-[11px] leading-tight text-[var(--c-text-muted)] sm:block">
								{activeItem.description}
							</div>
						) : null}
					</div>

					<ThemeToggle />
				</Header>

				<Content className="px-4 py-6 sm:px-6 lg:px-8">
					<div className="mx-auto w-full max-w-[1400px]">
						<Outlet />
					</div>
				</Content>
			</Layout>
		</Layout>
	);
}
